import { useState, useEffect, useCallback } from 'react';
import { TextInput, PasswordInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconLogin, IconMail, IconLock } from '@tabler/icons-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import EmailVerification from 'supertokens-auth-react/recipe/emailverification';
import Session from 'supertokens-auth-react/recipe/session';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '@/store/Store';
import { clientRoutes, serverPath } from '@/ApiPaths';
import {
  loadAuthenticatedUserProfile,
  resolvePostLoginPath,
  storeSsoReturn,
  peekSsoReturn,
  ROBOTOP_POPUP_LOGIN_MSG,
} from '@/services/Authentication';
import { getUnityHandoffTokens } from '@/Api';
import { getJson } from '@/services/httpClient';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import {
  AuthPageShell,
  AuthSubmitButton,
  AuthTextLink,
  AuthSecondaryButton,
  AuthPrimaryButton,
} from '@/components/auth/AuthPageShell';
import { getAuthInputStyles } from '@/components/auth/authInputStyles';
import authClasses from '@/components/auth/AuthPage.module.css';

interface LoginFormData {
  email: string;
  password: string;
}

async function isOpenerOriginAllowed(openerOrigin: string): Promise<boolean> {
  try {
    if (new URL(openerOrigin).origin === window.location.origin) return true;
  } catch {
    return false;
  }
  try {
    const probe = `${openerOrigin.replace(/\/$/, '')}/`;
    const checkUrl = `${serverPath}auth/redirect-allowed?return_url=${encodeURIComponent(probe)}`;
    const data = await getJson<{ allowed: boolean }>(checkUrl);
    return !!data.allowed;
  } catch {
    return false;
  }
}

/**
 * Popup "Login with Robotop": notify opener with Unity handoff tokens and close.
 */
async function finishPopupLogin(openerOrigin: string): Promise<boolean> {
  if (!window.opener || window.opener.closed) return false;
  if (!(await isOpenerOriginAllowed(openerOrigin))) {
    console.warn('Popup opener origin not allowed:', openerOrigin);
    return false;
  }

  try {
    const handoff = await getUnityHandoffTokens();
    window.opener.postMessage(
      {
        type: ROBOTOP_POPUP_LOGIN_MSG,
        status: 'ok',
        userId: handoff.userId,
        tokens: handoff.tokens,
      },
      openerOrigin
    );
  } catch (error) {
    console.error('Popup handoff failed:', error);
    window.opener.postMessage(
      { type: ROBOTOP_POPUP_LOGIN_MSG, status: 'error', error: 'handoff_failed' },
      openerOrigin
    );
    return false;
  }

  window.close();
  return true;
}

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popupDone, setPopupDone] = useState(false);
  const userProfile = useStore((state) => state.userProfile);
  const accentColor = userProfile.qrCodeColor || DEFAULT_QR_CODE_COLOR;
  const inputStyles = getAuthInputStyles(accentColor);

  const isPopup = searchParams.get('popup') === '1';
  const openerOrigin = searchParams.get('opener_origin') || '';

  const afterAuthSuccess = useCallback(async () => {
    if (isPopup && openerOrigin) {
      const closed = await finishPopupLogin(openerOrigin);
      if (closed) return;
      // Browsers often block window.close(); tokens were already posted — show manual close UI.
      setPopupDone(true);
      return;
    }
    const path = await resolvePostLoginPath(searchParams.get('returnTo'));
    navigate(path, { replace: true });
  }, [isPopup, openerOrigin, navigate, searchParams]);

  // Capture SSO params; bounce if already logged in (SSO redirect or popup).
  useEffect(() => {
    const returnUrl = searchParams.get('return_url');
    const state = searchParams.get('state');
    if (returnUrl && state) {
      storeSsoReturn(returnUrl, state);
    }

    const hasSsoIntent = !!(returnUrl && state) || !!peekSsoReturn();
    const hasPopupIntent = isPopup && !!openerOrigin;
    if (!hasSsoIntent && !hasPopupIntent) return;

    let cancelled = false;
    void (async () => {
      if (!(await Session.doesSessionExist())) return;
      const verificationStatus = await EmailVerification.isEmailVerified();
      if (cancelled) return;
      if (verificationStatus.status === 'OK' && verificationStatus.isVerified) {
        await afterAuthSuccess();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, isPopup, openerOrigin, afterAuthSuccess]);

  const form = useForm<LoginFormData>({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : t('login.invalidEmail', 'Invalid email'),
      password: (value) =>
        value.length >= 6 ? null : t('login.passwordMinLength', 'Password must be at least 6 characters'),
    },
  });

  const redirectUnverifiedUser = async () => {
    let message = t('login.emailNotVerifiedMessage');

    try {
      if (await Session.doesSessionExist()) {
        const sendResult = await EmailVerification.sendVerificationEmail();
        if (sendResult.status === 'OK') {
          message = t('login.verificationEmailSent');
        }
      }
    } catch (error) {
      console.error('Failed to send verification email after login:', error);
    }

    notifications.show({
      title: t('login.emailNotVerified'),
      message,
      color: 'yellow',
      icon: <IconMail size={16} />,
    });
    navigate(clientRoutes.loginVerifyEmail, { replace: true });
  };

  const handleSubmit = async (values: LoginFormData) => {
    setIsSubmitting(true);

    try {
      const response = await EmailPassword.signIn({
        formFields: [
          { id: 'email', value: values.email },
          { id: 'password', value: values.password },
        ],
      });

      if (response.status === 'OK') {
        useStore.getState().reset();
        await loadAuthenticatedUserProfile(response.user.id, values.email);

        const verificationStatus = await EmailVerification.isEmailVerified();
        if (verificationStatus.status !== 'OK' || !verificationStatus.isVerified) {
          await redirectUnverifiedUser();
          return;
        }

        await afterAuthSuccess();
        return;
      }

      if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        notifications.show({
          title: t('login.invalidCredentials'),
          message: '',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
        return;
      }

      if (response.status === 'FIELD_ERROR') {
        notifications.show({
          title: t('login.fieldError'),
          message: '',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
        return;
      }

      if (response.status === 'SIGN_IN_NOT_ALLOWED') {
        notifications.show({
          title: t('login.signInNotAllowed'),
          message: response.reason ?? '',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
        return;
      }

      notifications.show({
        title: t('login.loginError'),
        message: '',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } catch (error: unknown) {
      console.error('Login error:', error);
      notifications.show({
        title: t('login.loginError'),
        message: '',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageShell
      title={popupDone ? t('login.popupDoneTitle', 'You are signed in') : t('login.title')}
      subtitle={
        popupDone
          ? t('login.popupDoneSubtitle', 'You can close this window and return to the application.')
          : isPopup
            ? t('login.popupSubtitle', 'Sign in to continue to the partner application')
            : t('login.subtitle')
      }
      icon={<IconLogin size={22} stroke={1.75} />}
      accentColor={accentColor}
      footer={
        isPopup || popupDone ? undefined : (
          <>
            <p className={authClasses.footerText}>{t('login.signupLink')}</p>
            <AuthSecondaryButton onClick={() => navigate(clientRoutes.signup)} accentColor={accentColor}>
              {t('login.signupLinkAction')}
            </AuthSecondaryButton>
          </>
        )
      }
    >
      {popupDone ? (
        <div className={authClasses.formStack}>
          <AuthPrimaryButton
            type="button"
            accentColor={accentColor}
            onClick={() => {
              window.close();
            }}
          >
            {t('login.popupClose', 'Close window')}
          </AuthPrimaryButton>
        </div>
      ) : (
      <form onSubmit={form.onSubmit(handleSubmit)} className={authClasses.formStack}>
        <TextInput
          label={t('login.email')}
          placeholder={t('login.emailPlaceholder')}
          leftSection={<IconMail size={17} />}
          size="md"
          radius="md"
          styles={inputStyles}
          {...form.getInputProps('email')}
          required
        />

        <PasswordInput
          label={t('login.password')}
          placeholder={t('login.passwordPlaceholder')}
          leftSection={<IconLock size={17} />}
          size="md"
          radius="md"
          styles={inputStyles}
          {...form.getInputProps('password')}
          required
        />

        {!isPopup && (
          <div className={authClasses.linkRow}>
            <AuthTextLink
              onClick={() => navigate(clientRoutes.loginResetPassword)}
              accentColor={accentColor}
            >
              {t('login.forgotPassword')}
            </AuthTextLink>
          </div>
        )}

        <AuthSubmitButton loading={isSubmitting} accentColor={accentColor}>
          {t('login.loginButton')}
        </AuthSubmitButton>
      </form>
      )}
    </AuthPageShell>
  );
}
